import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [tokenStatus, setTokenStatus] = useState('checking');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }
    fetch(`${BACKEND_URL}/api/auth/guest-activation-valid?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.valid) {
          setEmail(d.email || '');
          setName(d.name || '');
          setTokenStatus('valid');
        } else {
          setTokenStatus('invalid');
        }
      })
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/activate-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // Auto-login the user.
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        toast.success('¡Cuenta activada! Redirigiendo a tu perfil...');
        setDone(true);
        setTimeout(() => {
          window.location.href = '/perfil';
        }, 1500);
      } else {
        toast.error(data.detail || data.message || 'No se pudo activar la cuenta');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f6f0] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border-2 border-black p-8" data-testid="activate-account-panel">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 items-center justify-center bg-black text-white rounded-full mb-3">
            <UserPlus className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Activa tu cuenta
          </h1>
          <p className="text-sm text-gray-500 mt-1">ARTHUR FITZ</p>
        </div>

        {tokenStatus === 'checking' && (
          <p className="text-center text-gray-600" data-testid="activate-checking">Comprobando enlace...</p>
        )}

        {tokenStatus === 'invalid' && !done && (
          <div className="text-center space-y-4" data-testid="activate-invalid">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="text-gray-800 font-semibold">Enlace no válido o caducado</p>
            <p className="text-sm text-gray-600">
              Los enlaces de activación son válidos durante 30 días y sólo pueden usarse una vez.
              Si ya tienes cuenta, puedes iniciar sesión desde el inicio. Si la necesitas reenviar,
              contáctanos en info@arthurfitz.com.
            </p>
            <Link to="/">
              <Button className="w-full bg-black text-white hover:bg-gray-800" data-testid="activate-home-btn">
                Volver al inicio
              </Button>
            </Link>
          </div>
        )}

        {tokenStatus === 'valid' && !done && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="activate-form">
            <div className="bg-[#f7f5ef] border-l-4 border-black p-4 text-sm">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  Bienvenido{name ? `, ${name.split(' ')[0]}` : ''}. Elige una contraseña y podrás
                  ver el historial de pedidos, acumular <strong>Fitz Points</strong> y acceder al
                  club <strong>ArtFitz</strong>.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="activation-email">Email</Label>
              <Input
                id="activation-email"
                type="email"
                value={email}
                readOnly
                disabled
                className="bg-gray-50"
                data-testid="activation-email-input"
              />
            </div>

            <div>
              <Label htmlFor="activation-password">Contraseña</Label>
              <Input
                id="activation-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                placeholder="Mínimo 6 caracteres"
                data-testid="activation-password-input"
              />
            </div>

            <div>
              <Label htmlFor="activation-confirm">Confirmar contraseña</Label>
              <Input
                id="activation-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                data-testid="activation-confirm-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-black text-white hover:bg-gray-800 tracking-wider"
              data-testid="activate-submit-btn"
            >
              {submitting ? 'Activando...' : 'ACTIVAR MI CUENTA'}
            </Button>
          </form>
        )}

        {done && (
          <div className="text-center space-y-4" data-testid="activate-done">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p className="font-semibold">¡Cuenta activada!</p>
            <p className="text-sm text-gray-600">Accediendo a tu perfil…</p>
          </div>
        )}
      </div>
    </div>
  );
}
